import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Axios 인스턴스 생성
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// 요청 인터셉터 (토큰 자동 추가)
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 응답 인터셉터 (에러 처리)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // 토큰 만료 또는 인증 실패
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// API 함수들
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
    changePassword: (data) => api.post('/auth/change-password', data),  // ⭐ PUT → POST
    updateProfile: (data) => api.put('/auth/profile', data),
};

export const projectAPI = {
    getAll: (params) => api.get('/projects', { params }),
    getById: (id) => api.get(`/projects/${id}`),
    create: (data) => api.post('/projects', data),
    update: (id, data) => api.put(`/projects/${id}`, data),
    updateFlowLink: (id, data) => api.patch(`/projects/${id}/flow-link`, data),
    delete: (id) => api.delete(`/projects/${id}`),
};

export const workLogAPI = {
    getAll: (params) => api.get('/work-logs', { params }),
    create: (data) => api.post('/work-logs', data),
    approve: (id, data) => api.put(`/work-logs/${id}/approve`, data),
};

export const workHourAPI = {
    getMy: (params) => api.get('/work-hours/my', { params }),
    create: (data) => api.post('/work-hours', data),
    update: (id, data) => api.put(`/work-hours/${id}`, data),
    delete: (id) => api.delete(`/work-hours/${id}`),
    getForApproval: (params) => api.get('/work-hours/for-approval', { params }),
    approve: (id, data) => api.post(`/work-hours/${id}/approve`, data),
};

export const projectMemberAPI = {
    getByProject: (projectId) => api.get(`/project-members/project/${projectId}`),
    getUserAllocation: (userId) => api.get(`/project-members/user/${userId}/allocation`),
    add: (projectId, data) => api.post(`/project-members/project/${projectId}`, data),
    reallocate: (id, data) => api.put(`/project-members/${id}/reallocate`, data),
    update: (id, data) => api.put(`/project-members/${id}`, data),
    remove: (id) => api.delete(`/project-members/${id}`),
};

export const expenseAPI = {
    getAll: (params) => api.get('/personal-expenses', { params }),
    create: (data) => api.post('/personal-expenses', data),
    update: (id, data) => api.put(`/personal-expenses/${id}`, data),
    delete: (id) => api.delete(`/personal-expenses/${id}`),
    pmApprove: (id, data) => api.post(`/personal-expenses/${id}/pm-approve`, data),
    financeProcess: (id, data) => api.post(`/personal-expenses/${id}/finance-process`, data),
};

export const dashboardAPI = {
    getUser: () => api.get('/dashboard/user'),
    getPM: () => api.get('/dashboard/pm'),
    getAdmin: () => api.get('/dashboard/admin'),  // ⭐ 추가
};

export const personalExpenseAPI = {
    getAll: (params) => api.get('/personal-expenses', { params }),
    getById: (id) => api.get(`/personal-expenses/${id}`),
    create: (data) => api.post('/personal-expenses', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    update: (id, data) => api.put(`/personal-expenses/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    delete: (id) => api.delete(`/personal-expenses/${id}`),
    pmApprove: (id) => api.post(`/personal-expenses/${id}/pm-approve`),
    financeProcess: (id) => api.post(`/personal-expenses/${id}/finance-process`),
    reject: (id, data) => api.post(`/personal-expenses/${id}/reject`, data),
};

// 프로젝트 수입/지출 API
export const projectTransactionAPI = {
    getAll: (params) => api.get('/project-transactions', { params }),
    getById: (id) => api.get(`/project-transactions/${id}`),
    getStats: (projectId) => api.get(`/project-transactions/stats/${projectId}`),
    create: (data) => api.post('/project-transactions', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    update: (id, data) => api.put(`/project-transactions/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    delete: (id) => api.delete(`/project-transactions/${id}`),
};

// 사용자 관리 API (관리자 전용)
export const userAPI = {
    getAll: (params) => api.get('/users', { params }),
    getSimple: () => api.get('/users/simple'), // PM, 부서장도 사용 가능
    getById: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
};

// 부서 관리 API (관리자 전용)
export const departmentAPI = {
    getAll: (params) => api.get('/departments', { params }),
    getById: (id) => api.get(`/departments/${id}`),
    create: (data) => api.post('/departments', data),
    update: (id, data) => api.put(`/departments/${id}`, data),
    delete: (id) => api.delete(`/departments/${id}`),
    addMember: (id, data) => api.post(`/departments/${id}/members`, data),
    removeMember: (deptId, userId) => api.delete(`/departments/${deptId}/members/${userId}`),
    // 부서 예산 및 수주 목표 관리
    getBudgets: (id, params) => api.get(`/departments/${id}/budgets`, { params }),
    upsertBudget: (id, data) => api.post(`/departments/${id}/budgets`, data),
    deleteBudget: (id, budgetId) => api.delete(`/departments/${id}/budgets/${budgetId}`)
};

// 재무 대시보드 API
export const financeDashboardAPI = {
    getDashboard: (params) => api.get('/finance-dashboard', { params }),
    // 프로젝트별 지출 집계 (외주비/자재비/경비/기타/내부인건비)
    getProjectCosts: (params) => api.get('/finance-dashboard/project-costs', { params }),
};

// 근태 관리 API
export const attendanceAPI = {
    getMyStatus: () => api.get('/attendance/my/status'),
    getMy: (params) => api.get('/attendance/my', { params }),
    create: (data) => api.post('/attendance', data),
    update: (id, data) => api.put(`/attendance/${id}`, data),
    delete: (id) => api.delete(`/attendance/${id}`),
    getAllUsersStatus: (params) => api.get('/attendance/admin/all-users', { params }),
    updateAnnualLeaveBalance: (userId, year, data) => api.put(`/attendance/admin/balance/${userId}/${year}`, data),
    getTeamCalendar: (params) => api.get('/attendance/team-calendar', { params }),
    approve: (attendanceId) => api.put(`/attendance/${attendanceId}/approve`),
    reject: (attendanceId, data) => api.put(`/attendance/${attendanceId}/reject`, data),
    getPending: (params) => api.get('/attendance/pending', { params }),
    // 연차 일괄등록 관련 API
    getBulkLeaveUsers: (params) => api.get('/attendance/bulk-leave/users', { params }),
    createBulkAnnualLeave: (data) => api.post('/attendance/bulk-leave', data),
    // 사용자별 연차 관리 API (관리자 전용)
    getUserAttendanceHistory: (userId, params) => api.get(`/attendance/admin/user/${userId}/history`, { params }),
    updateUserAttendance: (attendanceId, data) => api.put(`/attendance/admin/attendance/${attendanceId}`, data),
    createUserAttendance: (data) => api.post('/attendance/admin/user-attendance', data),
    deleteUserAttendance: (attendanceId) => api.delete(`/attendance/admin/attendance/${attendanceId}`),
};


export default api;